import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    LightningBoltIcon,
    MagnifyingGlassIcon,
    BarChartIcon,
    Cross2Icon,
} from '@radix-ui/react-icons';

const INTENTS = [
    {
        icon: LightningBoltIcon,
        iconBg: 'bg-violet-500/20',
        iconColor: 'text-violet-400',
        border: 'hover:border-violet-500/40',
        glow: 'hover:shadow-violet-500/10',
        label: 'Publish Articles',
        description: 'Write, schedule, and send new articles straight to your WordPress site. Check what\'s in your queue, generate new content, or review drafts ready to go live.',
        cta: 'Go to Topic Queue →',
        path: '/topic-queue',
    },
    {
        icon: MagnifyingGlassIcon,
        iconBg: 'bg-blue-500/20',
        iconColor: 'text-blue-400',
        border: 'hover:border-blue-500/40',
        glow: 'hover:shadow-blue-500/10',
        label: 'Research Keywords',
        description: 'Find the best keywords to write about, spot content your competitors are missing, and discover trending topics before they peak.',
        cta: 'Go to Keyword Overview →',
        path: '/keyword-overview',
    },
    {
        icon: BarChartIcon,
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        border: 'hover:border-emerald-500/40',
        glow: 'hover:shadow-emerald-500/10',
        label: 'Check Performance',
        description: 'See how your published articles are ranking, track your streak, measure traffic value, and find out which content is driving the most results.',
        cta: 'Go to Analytics →',
        path: '/analytics',
    },
];

const IntentPickerModal = ({ onDismiss }) => {
    const navigate = useNavigate();

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onDismiss]);

    const handlePick = (path) => {
        onDismiss();
        navigate(path);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up"
            onClick={onDismiss}
        >
            <div
                className="relative w-full max-w-xl bg-zinc-900/95 border border-white/[0.09] rounded-2xl shadow-2xl shadow-black/60 p-6"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close */}
                <button
                    onClick={onDismiss}
                    className="absolute top-4 right-4 p-1.5 rounded-lg text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.06] transition-all"
                >
                    <Cross2Icon className="h-4 w-4" />
                </button>

                {/* Header */}
                <div className="mb-6">
                    <h2 className="text-lg font-semibold text-zinc-100">What's your focus today?</h2>
                    <p className="text-sm text-zinc-500 mt-0.5">Pick one and we'll take you straight there.</p>
                </div>

                {/* Cards */}
                <div className="space-y-3">
                    {INTENTS.map(({ icon: Icon, iconBg, iconColor, border, glow, label, description, cta, path }) => (
                        <button
                            key={path}
                            onClick={() => handlePick(path)}
                            className={`w-full flex items-start gap-4 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] ${border} hover:bg-white/[0.06] shadow-lg ${glow} transition-all duration-200 text-left group`}
                        >
                            <div className={`p-2.5 rounded-xl ${iconBg} flex-shrink-0 mt-0.5`}>
                                <Icon className={`h-5 w-5 ${iconColor}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-zinc-100">{label}</p>
                                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{description}</p>
                                <p className="text-xs text-zinc-400 mt-2 group-hover:text-zinc-200 transition-colors">{cta}</p>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Footer */}
                <p className="text-center text-xs text-zinc-700 mt-5">
                    or press <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 border border-white/[0.07] text-zinc-500 text-[10px]">Esc</kbd> to go to Dashboard
                </p>
            </div>
        </div>
    );
};

export default IntentPickerModal;
